import {
  Direction,
  DraftDatatypeOperator,
  DraftFilter,
  DraftObjectOperator,
  ObjectOperatorType,
  OperatorType,
  Variable,
} from 'vet-bones/utils/queryBuilder';

import {
  GEOSPARQL_JSON_LITERAL_IRI,
  GEOSPARQL_WKT_LITERAL_IRI,
  RDFS_LABEL_IRI,
  RDF_TYPE_IRI,
  STARDOG_LABEL_IRI,
  XSD_BOOLEAN_IRI,
  XSD_DATE_IRI,
  XSD_DATE_TIME_IRI,
  XSD_DAY_TIME_DURATION_IRI,
  XSD_DECIMAL_IRI,
  XSD_DOUBLE_IRI,
  XSD_DURATION_IRI,
  XSD_FLOAT_IRI,
  XSD_INTEGER_IRI,
  XSD_STRING_IRI,
  XSD_TIME_IRI,
  XSD_YEAR_MONTH_DURATION_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { OBJECT_VARIABLE } from 'src/utils/stringify/rules/constants';

export const buildThenObjectFilter = ({
  objectIri,
  objectVariable,
  operator,
  subjectIri,
  subjectVariable,
  value,
}: {
  objectIri: string;
  objectVariable: string;
  operator: DraftObjectOperator;
  subjectIri: string;
  subjectVariable: string;
  value: Variable | string;
}): string => {
  const { direction, iri, type } = operator;
  const isOutbound = direction === Direction.OUTBOUND;

  const subject = subjectIri ? `<${subjectIri}>` : subjectVariable;
  const object = objectIri ? `<${objectIri}>` : objectVariable;

  switch (type) {
    case ObjectOperatorType.CONNECTED: {
      if (iri === RDF_TYPE_IRI && typeof value === 'object') {
        return `${subject} <${iri}> <${value.iri}> .`;
      }

      return isOutbound
        ? `${subject} <${iri}> ${object} .`
        : `${object} <${iri}> ${subject} .`;
    }

    default:
      return '';
  }
};

export const buildThenDatatypeFilter = ({
  operator,
  subjectIri,
  subjectVariable,
  value,
}: {
  operator: DraftDatatypeOperator;
  subjectIri: string;
  subjectVariable: string;
  value: string;
}): string => {
  const { iri, range } = operator;
  const subject = subjectIri ? `<${subjectIri}>` : subjectVariable;

  let typedValue = '';
  const rangeIri = range || XSD_STRING_IRI;
  switch (range) {
    case null:
    case XSD_STRING_IRI: {
      typedValue = `"${escapeDoubleQuotes(value)}"`;
      break;
    }
    case XSD_BOOLEAN_IRI: {
      typedValue = value;
      break;
    }

    case GEOSPARQL_JSON_LITERAL_IRI:
    case GEOSPARQL_WKT_LITERAL_IRI:
    case XSD_DAY_TIME_DURATION_IRI:
    case XSD_DECIMAL_IRI:
    case XSD_DOUBLE_IRI:
    case XSD_DURATION_IRI:
    case XSD_FLOAT_IRI:
    case XSD_INTEGER_IRI:
    case XSD_DATE_TIME_IRI:
    case XSD_DATE_IRI:
    case XSD_TIME_IRI:
    case XSD_YEAR_MONTH_DURATION_IRI: {
      typedValue = `"${escapeDoubleQuotes(value)}"^^<${rangeIri}>`;
      break;
    }

    default:
      break;
  }

  if (!typedValue) {
    return '';
  }

  const predicateIri = iri === STARDOG_LABEL_IRI ? RDFS_LABEL_IRI : iri;
  return `${subject} <${predicateIri}> ${typedValue} .`;
};

export const buildThenFilter = ({
  getInstanceIri,
  getVariable,
  operator,
  subject,
  value,
}: {
  getInstanceIri: (subject: Variable) => string;
  getVariable: (variable: string, id?: string) => string;
  operator: DraftDatatypeOperator | DraftObjectOperator;
  subject: Variable;
  value: DraftFilter['value'];
}): string | null => {
  const subjectIri = getInstanceIri(subject) || '';
  const subjectVariable = getVariable(OBJECT_VARIABLE, subject.id);

  // no instance operator type for then filters
  if (operator.operatorType === OperatorType.DATATYPE) {
    if (typeof value === 'string' || value === undefined || value === null) {
      return buildThenDatatypeFilter({
        operator,
        subjectIri,
        subjectVariable,
        value: value || '',
      });
    }

    return null;
  }

  if (value && typeof value !== 'string') {
    const objectIri = getInstanceIri(value) || '';
    const objectVariable = getVariable(OBJECT_VARIABLE, value.id);
    return buildThenObjectFilter({
      objectIri,
      objectVariable,
      operator,
      subjectIri,
      subjectVariable,
      value,
    });
  }

  return null;
};
